import type { ForwardedRef, ReactElement, Ref } from "react";
import { forwardRef, useId, useState } from "react";

import type { IDropdownChild, IDropDownItem } from "@similarweb/ui-components";
import { DropdownButton, EllipsisDropdownItem } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { StyledBaseDropdownButtonWrap } from "app/pages/sales-intelligence/sub-modules/advanced-search/components/common-styles";
import { buildDropdownSelectedItemsFromArray } from "app/pages/sales-intelligence/utils/dropdowns";

import BaseMultiselectDropdown from "../BaseMultiselectDropdown/BaseMultiselectDropdown";

import { DEFAULT_DD_WIDTH, DROPDOWN_NO_RESULTS_MESSAGE } from "./const";
import { StyledDropdownContainer } from "./styles";

export type IGenericDropDownItem<T extends string | number> = IDropDownItem & { id: T; partiallySelected?: boolean };

type DropdownWithMultiSelectProps<T extends string | number> = {
    elementId: string;
    width?: number | string;
    dropdownPopupHeight?: number;
    items: readonly IGenericDropDownItem<T>[];
    buttonText: string;
    buttonTextColor?: string;
    hasSearch?: boolean;
    searchPlaceHolder?: string;
    selectedItems: readonly T[] | undefined;
    disabledItems?: readonly T[] | undefined;
    onSelect: (item: T) => void;
    onToggle?: (isOpen: boolean) => void;
    disabled?: boolean;
    renderFooter?: () => ReactElement;
};

/** @deprecated use Combobox instead */
const MultiselectDropdown = <T extends string | number>(
    {
        width = DEFAULT_DD_WIDTH,
        dropdownPopupHeight,
        elementId,
        items,
        selectedItems,
        disabledItems,
        buttonText,
        buttonTextColor,
        onSelect,
        onToggle,
        searchPlaceHolder,
        hasSearch = false,
        disabled = false,
        renderFooter,
    }: DropdownWithMultiSelectProps<T>,
    ref: ForwardedRef<unknown>,
) => {
    const dropdownId = useId();
    const [isOpened, setIsOpened] = useState(false);
    const selectedIds = buildDropdownSelectedItemsFromArray(selectedItems ?? []);

    const handleClick = ({ id }: IDropDownItem) => {
        onSelect(id as T);
    };

    const handleToggle = (isOpen: boolean) => {
        setIsOpened(isOpen);
        onToggle?.(isOpen);
    };

    const dropdownItems: IDropdownChild[] = [
        <StyledBaseDropdownButtonWrap
            disabled={disabled}
            buttonTextColor={buttonTextColor}
            key={`${elementId}-button`}
        >
            <DropdownButton
                disabled={disabled}
                isOpen={isOpened}
            >
                {buttonText}
            </DropdownButton>
        </StyledBaseDropdownButtonWrap>,
        ...items.map((item) => (
            <EllipsisDropdownItem
                key={item.id}
                id={item.id as string}
                showCheckBox
                partiallySelected={item.partiallySelected}
                disabled={disabledItems?.includes(item.id)}
            >
                {item.text}
            </EllipsisDropdownItem>
        )),
    ];

    return (
        <StyledDropdownContainer
            id={dropdownId}
            width={width}
            data-automation-id={elementId}
        >
            <BaseMultiselectDropdown
                ref={ref}
                width={width}
                dropdownPopupHeight={dropdownPopupHeight}
                appendTo={`#${CSS.escape(dropdownId)}`}
                selectedIds={selectedIds}
                closeOnItemClick={false}
                onClick={handleClick}
                noResultsMessage={translate(DROPDOWN_NO_RESULTS_MESSAGE)}
                onToggle={handleToggle}
                hasSearch={hasSearch}
                searchPlaceHolder={searchPlaceHolder}
                virtualize={dropdownItems.length > 10}
                disabled={disabled}
                footerComponent={renderFooter}
            >
                {dropdownItems}
            </BaseMultiselectDropdown>
        </StyledDropdownContainer>
    );
};

export default forwardRef(MultiselectDropdown) as <T extends string | number>(
    props: DropdownWithMultiSelectProps<T> & { ref?: Ref<unknown> },
) => ReactElement;
